import React, { useEffect } from "react"
import { Box, IconButton, Tooltip, Divider, Paper, useTheme } from "@mui/material"
import { styled } from "@mui/system"
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote
} from "@mui/icons-material"
import Placeholder from "@tiptap/extension-placeholder"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import Link from "@tiptap/extension-link"
import { TextStyle, Color } from "@tiptap/extension-text-style"

const Toolbar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1, 2),
  backgroundColor: "#292929",
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  "& .MuiIconButton-root": {
    padding: theme.spacing(0.5),
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  "& .MuiDivider-root": {
    height: "18px",
    margin: theme.spacing(0, 1),
    borderColor: "rgba(255,255,255,0.1)"
  }
}))

const EditorContainer = styled(Paper)(({ theme }) => ({
  border: `1.5px solid #423B3B`,
  borderRadius: "16px",
  overflow: "hidden",
  backgroundColor: theme.palette.drawer.main,
  boxShadow: "none"
}))

const ContentArea = styled(Box)(({ theme }) => ({
  padding: "16px",
  minHeight: "120px",

  "& .ProseMirror": {
    outline: "none",
    color: "#fff",
    fontSize: "1.1rem",
    lineHeight: 1.6
  },

  "& ul, & ol": {
    margin: theme.spacing(1, 0),
    paddingLeft: theme.spacing(3)
  },

  "& li": {
    marginBottom: theme.spacing(0.5)
  },

  "& blockquote": {
    borderLeft: `4px solid ${theme.palette.secondary.main}`,
    paddingLeft: theme.spacing(2),
    margin: theme.spacing(1, 0),
    fontStyle: "italic",
    color: "rgba(255,255,255,0.7)"
  },

  "& pre": {
    backgroundColor: "rgba(255,255,255,0.05)",
    padding: theme.spacing(1.5),
    borderRadius: "8px",
    fontFamily: "monospace",
    margin: theme.spacing(1, 0)
  },
  "& .ProseMirror p.is-editor-empty:first-of-type::before": {
    content: "attr(data-placeholder)",
    float: "left",
    color: "rgba(255,255,255,0.3)",
    pointerEvents: "none",
    height: 0
  },

  "& .ProseMirror > p:last-child:has(> br:only-child)": {
    display: "none"
  },
  "& .ProseMirror > p:has(> br:only-child) + ul, & .ProseMirror > p:has(> br:only-child) + ol, & .ProseMirror > p:has(> br:only-child) + blockquote":
    {
      marginTop: 0
    },
  "& .ProseMirror > p:has(> br:only-child):has(+ ul), & .ProseMirror > p:has(> br:only-child):has(+ ol), & .ProseMirror > p:has(> br:only-child):has(+ blockquote)":
    {
      display: "none"
    }
}))

const AppRichTextEditor = ({
  value,
  onChange,
  onBlur,
  placeholder,
  name,
  error,
  helperText,
  editable = true,
  sx = {}
}) => {
  const theme = useTheme()

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      TextStyle,
      Color,
      Placeholder.configure({
        placeholder: placeholder || "Write something..."
      })
    ],
    content: value || "",
    editable,
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor }) => {
      let html = editor.getHTML()
      html = html.replace(/<p><\/p>(\s*<(?:ul|ol|blockquote))/g, "$1")
      html = html.replace(/(<p><\/p>)+$/, "")
      if (html === "<p></p>" || html === "") html = ""
      onChange({ target: { name, value: html } })
    }
  })

  useEffect(() => {
    if (editor && !editor.isFocused) {
      const currentContent = editor.getHTML()
      const isBothEmpty =
        (!value || value === "" || value === "<p></p>") && currentContent === "<p></p>"
      if (value !== currentContent && !isBothEmpty) {
        editor.commands.setContent(value || "")
      }
    }
  }, [value, editor])

  if (!editor) return null

  return (
    <Box sx={sx}>
      <EditorContainer elevation={0} theme={theme}>
        {editable && (
          <Toolbar theme={theme}>
            <Tooltip title="Bold">
              <IconButton
                disableRipple
                sx={{
                  backgroundColor: editor.isActive("bold")
                    ? theme.palette.secondary.main
                    : "transparent",
                  color: theme.palette.text.primary,

                  "&:hover": {
                    backgroundColor: `${
                      editor.isActive("bold") ? theme.palette.secondary.main : "transparent"
                    } !important`
                  }
                }}
                onClick={() => editor.chain().focus().toggleBold().run()}>
                <FormatBold fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Italic">
              <IconButton
                disableRipple
                sx={{
                  backgroundColor: editor.isActive("italic")
                    ? theme.palette.secondary.main
                    : "transparent",
                  color: theme.palette.text.primary,

                  "&:hover": {
                    backgroundColor: `${
                      editor.isActive("italic") ? theme.palette.secondary.main : "transparent"
                    } !important`
                  }
                }}
                onClick={() => editor.chain().focus().toggleItalic().run()}>
                <FormatItalic fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Underline">
              <IconButton
                disableRipple
                sx={{
                  backgroundColor: editor.isActive("underline")
                    ? theme.palette.secondary.main
                    : "transparent",
                  color: theme.palette.text.primary,

                  "&:hover": {
                    backgroundColor: `${
                      editor.isActive("underline") ? theme.palette.secondary.main : "transparent"
                    } !important`
                  }
                }}
                onClick={() => editor.chain().focus().toggleUnderline().run()}>
                <FormatUnderlined fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Strikethrough">
              <IconButton
                disableRipple
                sx={{
                  backgroundColor: editor.isActive("strike")
                    ? theme.palette.secondary.main
                    : "transparent",
                  color: theme.palette.text.primary,

                  "&:hover": {
                    backgroundColor: `${
                      editor.isActive("strike") ? theme.palette.secondary.main : "transparent"
                    } !important`
                  }
                }}
                onClick={() => editor.chain().focus().toggleStrike().run()}>
                <StrikethroughS fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box sx={{ p: 0.5 }}>
              <Divider
                orientation="vertical"
                flexItem
                variant="middle"
                color={theme.palette.text.primary}
              />
            </Box>

            <Tooltip title="Bullet List">
              <IconButton
                disableRipple
                sx={{
                  backgroundColor: editor.isActive("bulletList")
                    ? theme.palette.secondary.main
                    : "transparent",
                  color: theme.palette.text.primary,

                  "&:hover": {
                    backgroundColor: `${
                      editor.isActive("bulletList") ? theme.palette.secondary.main : "transparent"
                    } !important`
                  }
                }}
                onClick={() => editor.chain().focus().toggleBulletList().run()}>
                <FormatListBulleted fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Numbered List">
              <IconButton
                disableRipple
                sx={{
                  backgroundColor: editor.isActive("orderedList")
                    ? theme.palette.secondary.main
                    : "transparent",
                  color: theme.palette.text.primary,

                  "&:hover": {
                    backgroundColor: `${
                      editor.isActive("orderedList") ? theme.palette.secondary.main : "transparent"
                    } !important`
                  }
                }}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                <FormatListNumbered fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ p: 0.5 }}>
              <Divider
                orientation="vertical"
                flexItem
                variant="middle"
                color={theme.palette.text.primary}
              />
            </Box>

            <IconButton
              disableRipple
              sx={{
                backgroundColor: editor.isActive("blockquote")
                  ? theme.palette.secondary.main
                  : "transparent",
                color: theme.palette.text.primary,

                "&:hover": {
                  backgroundColor: `${
                    editor.isActive("blockquote") ? theme.palette.secondary.main : "transparent"
                  } !important`
                }
              }}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}>
              <FormatQuote fontSize="small" />
            </IconButton>

            {/* <Tooltip title="Link">
              <IconButton
                disableRipple
                onClick={setLink}
                sx={{
                  backgroundColor: editor.isActive("link")
                    ? theme.palette.secondary.main
                    : "transparent",
                  color: theme.palette.text.primary,

                  "&:hover": {
                    backgroundColor: `${
                      editor.isActive("link") ? theme.palette.secondary.main : "transparent"
                    } !important`
                  }
                }}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip> */}
          </Toolbar>
        )}

        <ContentArea>
          <EditorContent editor={editor} onBlur={onBlur} />
        </ContentArea>
      </EditorContainer>

      {error && (
        <Box sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5, ml: 1.5 }}>{helperText}</Box>
      )}
    </Box>
  )
}

export default AppRichTextEditor
